import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CYCLE_STATUS_TO_NUMBER, CYCLE_TAB_BY_STATUS_QUERY_KEY, DEFAULT_STATUS_TAB, NO_TAB_SELECTED, TAB_NUMBER_TO_STATUS } from '../../../../ContractScreensView/constants';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import { usePolling } from '../../../../../hooks/usePolling';
import { CyclesCountResponse } from '../../../../../types/cycle';
import CycleStatus from '../../../../../enums/cycle-status.enum';
import { IContractScreensTabsInteractor } from '../../../../ContractScreensView/modules/Tabs/interactor';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../../constants/common';
import { IContractTabsInteractorProps } from '../interfaces/iteracor';

const useLocationContractCyclesTabs = (props: IContractTabsInteractorProps): IContractScreensTabsInteractor => {
  const { clientId } = useParams();
  const [activeTab, setActiveTab] = props.tabStatePayload;

  const [activeCycles, setActiveCycles] = useState<number>(null);
  const [inactiveCycles, setInactiveCycles] = useState<number>(null);
  const [completedCycles, setCompletedCycles] = useState<number>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const { cyclesCount } = useAppSelector((state) => state.cycles);

  const {
    locations: { getLocation },
    cycles: {
      getCyclesCountByLocation,
      setSearchCycleQuery,
      resetCompletedCycles,
      resetInactiveCycles,
      resetScreenCycles,
      resetScreenlessCycles,
      resetSearchCycleQuery,
    },
  } = useAppDispatch();

  const updateStates = (data: CyclesCountResponse | null) => {
    if (!data) {
      return;
    }

    setActiveCycles(data.active);
    setInactiveCycles(data.inactive);
    setCompletedCycles(data.completed);
  };

  const fetchDataByLocation = async (locationId: string) => {
    try {
      setIsLoading(true);

      await getCyclesCountByLocation({ locationId });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const setStatusQuery = (status: CycleStatus) => {
    setSearchParams({
      [CYCLE_TAB_BY_STATUS_QUERY_KEY]: status,
    });
  };

  const setupDefaultTab = () => {
    setStatusQuery(DEFAULT_STATUS_TAB);
  };

  const setActiveTabByParams = (urlSearchParams: URLSearchParams) => {
    const statusParam = urlSearchParams.get(CYCLE_TAB_BY_STATUS_QUERY_KEY);

    const isValidStatus = statusParam && statusParam in CYCLE_STATUS_TO_NUMBER;

    if (!isValidStatus) {
      if (activeTab === NO_TAB_SELECTED) {
        setupDefaultTab();
      } else {
        const currentStatus = TAB_NUMBER_TO_STATUS[activeTab];
        setStatusQuery(currentStatus);
      }
      return;
    }

    const currentActiveTab = CYCLE_STATUS_TO_NUMBER[statusParam];
    setActiveTab(currentActiveTab);
  };

  const onChangeActiveTab: IContractScreensTabsInteractor['onChangeTab'] = (value: number) => {
    if (!(value in TAB_NUMBER_TO_STATUS)) {
      return;
    }

    const currentStatus = TAB_NUMBER_TO_STATUS[value];

    setStatusQuery(currentStatus);
  };

  const resetStore = () => {
    const fns = [
      resetCompletedCycles,
      resetInactiveCycles,
      resetScreenCycles,
      resetScreenlessCycles,
      resetSearchCycleQuery,
    ];

    fns.forEach((fn) => fn());
  };

  usePolling(() => fetchDataByLocation(clientId), EXTENDED_INTERVAL_UPDATE_DATA, [clientId, searchParams]);

  useEffect(() => {
    getLocation(clientId);
  }, [clientId]);

  useEffect(() => {
    setActiveTabByParams(searchParams);
  }, [searchParams]);

  useEffect(() => {
    updateStates(cyclesCount);
  }, [cyclesCount]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, []);

  return {
    activeCycles,
    inactiveCycles,
    completedCycles,
    isLoading,
    activeTab,
    onChangeTab: onChangeActiveTab,
    onChangeSearch: setSearchCycleQuery,
  };
};

export default useLocationContractCyclesTabs;
